/**
 * „Mehr" — der vierte Bereich, `07` §7.7.
 *
 * Das Dokument listet: Bürochat, Wissen, Kalender, Kontakte, KI-Assistent,
 * Synchronisationsprobleme, Einstellungen und Logout.
 *
 * **Die Synchronisationsprobleme stehen oben, nicht unten.** In der Aufzählung
 * des Dokuments kommen sie an sechster Stelle; auf dem Bildschirm gehören sie
 * nach vorn, weil sie das einzige sind, was eine Handlung verlangt. Alles
 * andere ist Nachschlagen.
 *
 * **Der KI-Assistent fehlt.** ADR-017 hält ihn vollständig hinter dem Piloten,
 * und `features.ai` steht im Kundenstack auf `false`. Ein Menüpunkt, der dann
 * ins Leere führt, wäre ein Versprechen, das die Anlage nicht einlöst —
 * dieselbe Überlegung wie in der Portalnavigation.
 */

import { Link } from "react-router";

import { austauschadresse, findeAuftrag, findePerson } from "@hwc/demo-data";
import type { Erscheinung } from "@hwc/ui";
import { formatResourceRef } from "@hwc/contracts";

import { useMonteur } from "./Sitzung.js";
import { Grossknopf, Knopfleiste, Leer, datum, uhrzeit } from "./bausteine.js";

export function Mehr({
  erscheinung,
  setzeErscheinung,
  zuruecksetzen,
}: {
  erscheinung: Erscheinung;
  setzeErscheinung: (e: Erscheinung) => void;
  zuruecksetzen: () => void;
}) {
  const {
    bestand,
    monteur,
    lokal,
    online,
    synchronisiere,
    verwirfBefunde,
    wechsleMonteur,
  } = useMonteur();

  const monteure = bestand.personen.filter((p) => p.rollen.includes("technician"));

  const meineRaeume = bestand.chatraeume.filter((r) =>
    r.teilnehmer.some((t) => formatResourceRef(t) === formatResourceRef(monteur.ref)),
  );

  return (
    <>
      <h1>Mehr</h1>

      {/* ───────────────── Synchronisation ───────────────── */}
      <h2>Synchronisation</h2>

      {/*
        **Welcher Dienst gefragt wird, steht hier.**

        Der Austauschdienst läuft im Entwicklungsserver des Portals, Vorgabe
        Port 4300. Weicht das Portal auf einen anderen aus, weil der belegt
        ist, redet diese App weiter mit 4300 — und findet dort womöglich ein
        **anderes** Portal, etwa das, das die Oberflächenprüfung gestartet hat.

        Genau das ist am 05.09.2026 beim Messen passiert: Eine Stunde lang
        ging alles an die falsche Stelle, und weil dort ein Dienst antwortete,
        sah es aus wie ein Fehler in der App. Der Kommentar in `main.tsx` sagt,
        eine App, die stillschweigend den falschen Dienst befragt, sei
        schlimmer als eine, die keinen findet — dann muss sie auch sagen,
        welchen sie befragt.
      */}
      <p className="leise" data-hinweis="austausch">
        Gegenstelle: <code>{austauschadresse()}</code>
        {" · "}
        <a href={`?austausch=${encodeURIComponent(austauschadresse())}`}>
          Andere Adresse über <code>?austausch=</code>
        </a>
      </p>

      {lokal.warteschlange.length === 0 && lokal.befunde.length === 0 ? (
        <p className="leer leise">Alles gesendet. Nichts wartet.</p>
      ) : (
        <>
          {lokal.warteschlange.length > 0 && (
            <>
              <p>
                <strong>{lokal.warteschlange.length}</strong>{" "}
                {lokal.warteschlange.length === 1 ? "Änderung wartet" : "Änderungen warten"}{" "}
                auf Upload.
              </p>
              <ul className="postenliste">
                {lokal.warteschlange.map((op) => (
                  <li key={op.id}>
                    <span>
                      {beschreibe(op.idempotencyKey)}
                      <span className="leise"> · {op.mutation}</span>
                    </span>
                    <span>{uhrzeit(op.erstelltAm)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {/*
            Befunde: was abgelehnt wurde oder in den Konflikt lief. `04` §4.12
            verlangt bei fehlender Berechtigung ausdrücklich, lokal nicht mehr
            erlaubte Inhalte zu entfernen — bei allen anderen Ablehnungen gilt
            das Gegenteil, und deshalb steht hier, was mit den Daten geschah.
          */}
          {lokal.befunde.length > 0 && (
            <>
              <h3>Nicht angenommen</h3>
              <ul className="postenliste">
                {lokal.befunde.map(({ op, befund }) => (
                  <li key={op.id} className="postenliste__fern">
                    <span>
                      {beschreibe(op.idempotencyKey)}
                      <br />
                      <span className="leise">{befund.grund}</span>
                      <br />
                      <span className="leise">
                        {befund.lokalLoeschen
                          ? "Lokale Erfassung wurde entfernt (04 §4.12)."
                          : "Ihre Erfassung bleibt erhalten."}
                      </span>
                    </span>
                    <span className="abzeichen abzeichen--fehler">
                      {befund.entscheidung}
                    </span>
                  </li>
                ))}
              </ul>
              <Knopfleiste>
                <Grossknopf onClick={verwirfBefunde}>Meldungen abhaken</Grossknopf>
              </Knopfleiste>
            </>
          )}
        </>
      )}

      <Knopfleiste>
        <Grossknopf
          art="haupt"
          onClick={synchronisiere}
          disabled={!online || lokal.warteschlange.length === 0}
        >
          {online ? "Jetzt senden" : "Kein Netz"}
        </Grossknopf>
      </Knopfleiste>

      {!online && lokal.warteschlange.length > 0 && (
        <p className="leise">
          Im Funkloch bleibt alles liegen und geht raus, sobald wieder Netz da ist. Sie
          können weiterarbeiten — das ist der Sinn der Sache (<code>07</code> §7.9).
        </p>
      )}

      {/* ───────────────── Bürochat ───────────────── */}
      <h2>Bürochat</h2>
      {meineRaeume.length === 0 ? (
        <Leer text="Sie sind in keinem Auftragsraum." />
      ) : (
        meineRaeume.map((r) => {
          const auftrag = findeAuftrag(bestand, r.auftrag);
          const beitraege = bestand.chatbeitraege.filter(
            (b) => formatResourceRef(b.raum) === formatResourceRef(r.ref),
          );
          return (
            <section key={r.ref.opaqueId} className="chatraum">
              <h3>{r.name}</h3>
              {beitraege.slice(-4).map((b) => (
                <p key={b.ref.opaqueId} className="chatbeitrag">
                  <strong>{findePerson(bestand, b.autor)?.kuerzel}</strong> {b.text}
                  <span className="leise"> · {uhrzeit(b.gesendet)}</span>
                </p>
              ))}
              {auftrag && (
                <p className="leise">
                  <Link to={`/einsaetze`}>Auftrag {auftrag.nummer}</Link>
                </p>
              )}
            </section>
          );
        })
      )}
      <p className="leise fussnote">
        Text, Bilder und Sprachnachrichten. Anrufe und Videokonferenz sind nach ADR-018
        nicht Bestandteil von Version 1 — für Sprache bleibt das Telefon.
      </p>

      {/* ───────────────── Wissen ───────────────── */}
      <h2>Wissen</h2>
      <ul className="postenliste">
        {bestand.ablage
          .filter((o) => o.art === "wissen")
          .map((o) => (
            <li key={o.ref.opaqueId}>
              <span>{o.name}</span>
              <span
                className={`abzeichen abzeichen--${
                  o.offline === "vorgeladen" ? "erfolg" : "neutral"
                }`}
              >
                {o.offline === "vorgeladen" ? "offline" : "online"}
              </span>
            </li>
          ))}
      </ul>

      {/* ───────────────── Kontakte ───────────────── */}
      <h2>Kontakte</h2>
      <p className="leise">
        Nur die für Monteure freigegebenen Nummern — <code>07</code> §7.9 lädt keine
        anderen vor.
      </p>
      <ul className="postenliste">
        {bestand.kunden.flatMap((k) =>
          k.kontakte
            .filter((kontakt) => kontakt.fuerMonteur && kontakt.telefon)
            .map((kontakt) => (
              <li key={`${k.nummer}-${kontakt.name}`}>
                <span>
                  {kontakt.name}
                  <span className="leise"> · {k.name}</span>
                </span>
                <a href={`tel:${kontakt.telefon!.replace(/\s/g, "")}`}>
                  {kontakt.telefon}
                </a>
              </li>
            )),
        )}
      </ul>

      {/* ───────────────── Kalender ───────────────── */}
      <h2>Kalender</h2>
      <ul className="postenliste">
        {bestand.termine
          .filter((t) =>
            t.teilnehmer.some((r) => formatResourceRef(r) === formatResourceRef(monteur.ref)),
          )
          .map((t) => (
            <li key={t.ref.opaqueId}>
              <span>
                {t.titel}
                {t.ort && <span className="leise"> · {t.ort}</span>}
              </span>
              <span>
                {datum(t.von)} {uhrzeit(t.von)}
              </span>
            </li>
          ))}
      </ul>

      {/* ───────────────── Einstellungen ───────────────── */}
      <h2>Einstellungen</h2>

      <label className="formular__feld">
        Angemeldet als
        {/*
          Wie im Portal: keine Anmeldung, aber eine **sichtbare** Wahl. Wer
          die Tagesansicht von drei Monteuren vergleichen will, braucht sie —
          und eine angenommene Identität wäre unsichtbar und deshalb die
          schlechtere Lösung.
        */}
        <select
          value={monteur.ref.opaqueId}
          onChange={(e) => {
            const neu = monteure.find((p) => p.ref.opaqueId === e.target.value);
            if (neu) wechsleMonteur(neu);
          }}
        >
          {monteure.map((p) => (
            <option key={p.ref.opaqueId} value={p.ref.opaqueId}>
              {p.name}
              {p.verfuegbar === false ? " (abwesend)" : ""}
            </option>
          ))}
        </select>
      </label>

      <label className="formular__feld">
        Darstellung
        <select
          value={erscheinung}
          onChange={(e) => setzeErscheinung(e.target.value as Erscheinung)}
        >
          <option value="hell">Hell</option>
          <option value="dunkel">Dunkel</option>
        </select>
      </label>
      <p className="leise">
        Hell ist die Vorgabe: Draußen im Sonnenlicht ist ein dunkles Bild unlesbar, ein
        helles im Dunkeln blendet zwar, bleibt aber lesbar.
      </p>

      <h2>Diesen Probierstand zurücksetzen</h2>
      <p className="leise">
        Löscht alle lokal erfassten Änderungen — Zustandswechsel, Zeiten, Material,
        Fotos, Notizen — und lädt neu. Der Bestand selbst ist unveränderlich und kommt
        unverändert zurück.
      </p>
      <Knopfleiste>
        <Grossknopf art="gefahr" onClick={zuruecksetzen}>
          Alles zurücksetzen
        </Grossknopf>
      </Knopfleiste>
    </>
  );
}

/**
 * Aus einem Idempotenzschlüssel wird ein lesbarer Satz.
 *
 * Der Schlüssel ist `<ref>#<vorgang>` — technisch richtig und für einen
 * Monteur unlesbar. „urn:handwerker:demo:vikunja:task:e-8801#zustand:anfahrt"
 * beantwortet die Frage „was hängt da eigentlich" nicht.
 */
function beschreibe(schluessel: string): string {
  const vorgang = schluessel.split("#")[1] ?? schluessel;
  if (vorgang.startsWith("zustand:")) {
    return `Statuswechsel auf „${vorgang.slice("zustand:".length)}"`;
  }
  if (vorgang.startsWith("schritt:")) {
    return vorgang.endsWith(":erledigt")
      ? "Arbeitsschritt abgehakt"
      : "Arbeitsschritt wieder geöffnet";
  }
  if (vorgang.startsWith("zeit:")) return "Zeitbuchung";
  if (vorgang.startsWith("material:")) return "Materialposten";
  if (vorgang.startsWith("foto:")) return "Foto";
  if (vorgang === "notiz") return "Tätigkeitsnotiz";
  return vorgang;
}
